import { useState, useEffect, useCallback } from 'react';
import { listAdminUsers, createAdminUser } from '../services/adminUserService';
import { useAppConfig } from '../context/AppConfigContext';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  FaUserShield, FaPlus, FaEnvelope, FaUser, FaCalendarAlt,
  FaCopy, FaCheck, FaExclamationTriangle,
} from 'react-icons/fa';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  const seconds = timestamp._seconds ?? timestamp.seconds;
  if (typeof seconds !== 'number') return 'N/A';
  return new Date(seconds * 1000).toLocaleDateString('en-AU', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const AdminUsersPage = () => {
  const { config, loading: configLoading } = useAppConfig();
  const isSuperAdmin = config?.isSuperAdmin === true;

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Create form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [formError, setFormError] = useState(null);

  // Confirmation step before the write
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // The one-time credential shown after a successful create
  const [createdCredential, setCreatedCredential] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAdminUsers();
      setAdmins(data.admins || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin users.');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) fetchAdmins();
  }, [isSuperAdmin, fetchAdmins]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(null), 6000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (configLoading) {
    return <div className="container mx-auto px-4 py-8 text-center text-gray-500">Loading...</div>;
  }

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-lg mx-auto bg-white shadow-md rounded-lg p-6 text-center">
          <FaExclamationTriangle className="text-3xl text-yellow-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Access denied</h1>
          <p className="text-sm text-gray-600">
            This module is only available to the super admin account.
          </p>
        </div>
      </div>
    );
  }

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setFormError(null);
  };

  const handleOpenForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    if (isCreating) return;
    setIsFormOpen(false);
    setIsConfirmOpen(false);
    resetForm();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (!firstName.trim()) {
      setFormError('First name is required.');
      return;
    }
    if (!lastName.trim()) {
      setFormError('Last name is required.');
      return;
    }
    if (admins.some((a) => (a.email || '').trim().toLowerCase() === normalizedEmail)) {
      setFormError(`${normalizedEmail} is already an admin user.`);
      return;
    }
    setFormError(null);
    setIsConfirmOpen(true);
  };

  const handleConfirmCreate = async () => {
    setIsCreating(true);
    setFormError(null);
    try {
      const result = await createAdminUser({
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setIsConfirmOpen(false);
      setIsFormOpen(false);
      resetForm();
      setCreatedCredential(result);
      setCopied(false);
      setSuccessMessage(`Admin user ${result.email} created.`);
      await fetchAdmins();
    } catch (err) {
      setFormError(err.message || 'Failed to create admin user.');
      setIsConfirmOpen(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!createdCredential?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(createdCredential.temporaryPassword);
      setCopied(true);
    } catch (err) {
      console.error('Could not copy the temporary password:', err);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaUserShield className="mr-3" /> Admin Users
        </h1>
        <button
          onClick={handleOpenForm}
          className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
        >
          <FaPlus className="mr-2" />
          Create Admin User
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4 max-w-2xl">
        Admin users can sign in to the back office. New accounts start with a temporary password and
        must change it at first login. There is no delete function here.
      </p>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded" role="status">
          {successMessage}
        </div>
      )}

      {loading && <p className="text-center text-gray-500 py-10">Loading admin users...</p>}

      {error && !loading && (
        <div className="text-center text-red-700 bg-red-100 border border-red-300 p-4 rounded mb-4">
          <p className="mb-2">{error}</p>
          <button onClick={fetchAdmins} className="underline font-medium">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
          {admins.length === 0 ? (
            <p className="text-center text-gray-500 p-6">No admin users found.</p>
          ) : (
            <table className="min-w-full leading-normal">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                  <th className="px-5 py-3 border-b-2 border-gray-200"><FaEnvelope className="inline mr-1" /> Email Address</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200"><FaUser className="inline mr-1" /> First Name</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200"><FaUser className="inline mr-1" /> Last Name</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200"><FaCalendarAlt className="inline mr-1" /> Date Created</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.uid} className="hover:bg-gray-50">
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-nowrap">{admin.email || '—'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-nowrap">{admin.firstName || '—'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-nowrap">{admin.lastName || '—'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-nowrap">{formatTimestamp(admin.createdAt)}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create form */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-admin-title"
            className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
          >
            <h3 id="create-admin-title" className="text-lg font-medium leading-6 text-gray-900 mb-4">Create Admin User</h3>
            <form onSubmit={handleFormSubmit} noValidate>
              <div className="mb-4">
                <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700">Email Address *</label>
                <input
                  type="email"
                  id="admin-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                  placeholder="admin@example.com"
                  disabled={isCreating}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label htmlFor="admin-first-name" className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    id="admin-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    placeholder="Jane"
                    disabled={isCreating}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="admin-last-name" className="block text-sm font-medium text-gray-700">Last Name *</label>
                  <input
                    type="text"
                    id="admin-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    placeholder="Doe"
                    disabled={isCreating}
                    required
                  />
                </div>
              </div>

              {formError && !isConfirmOpen && (
                <p className="text-sm text-red-600 mb-3">{formError}</p>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50"
                  disabled={isCreating}
                >
                  Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation before the write */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => { if (!isCreating) setIsConfirmOpen(false); }}
        onConfirm={handleConfirmCreate}
        title="Create this admin user?"
        message={`Email: ${email.trim().toLowerCase()} — Name: ${firstName.trim()} ${lastName.trim()}. A temporary password will be generated and shown once.`}
        isLoading={isCreating}
        loadingText="Creating..."
        confirmText="Create Admin"
        variant="primary"
      />

      {/* One-time temporary password */}
      {createdCredential && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="created-admin-title"
            className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
          >
            <h3 id="created-admin-title" className="text-lg font-medium leading-6 text-gray-900 mb-2 flex items-center">
              <FaCheck className="text-green-500 mr-2" /> Admin user created
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Share this temporary password with {createdCredential.email} securely. It is shown once
              and must be changed at first login.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 min-w-0 truncate bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm">
                {createdCredential.temporaryPassword}
              </code>
              <button
                type="button"
                onClick={handleCopyPassword}
                className="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150 inline-flex items-center"
              >
                {copied ? <FaCheck className="mr-1 text-green-600" /> : <FaCopy className="mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCreatedCredential(null)}
                className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
